import React, { useState, useEffect } from 'react';
import { X, Save, Calculator } from 'lucide-react';
import { supabase } from '../../../lib/supabase';


interface AddPaymentPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave?: () => void;
}

interface Installment {
    date: string;
    amount: number;
    status: 'pending' | 'paid';
    paid_at?: string | null;
}

const AddPaymentPlanModal: React.FC<AddPaymentPlanModalProps> = ({ isOpen, onClose, onSave }) => {
    const [students, setStudents] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        studentId: '',
        totalAmount: '',
        currency: 'TRY',
        paymentType: 'installment' as 'full' | 'installment',
        installmentCount: 3,
        firstPaymentDate: new Date().toISOString().split('T')[0],
        notes: '',
        companyEntity: ''
    });

    const [installments, setInstallments] = useState<Installment[]>([]);

    useEffect(() => {
        if (isOpen) {
            fetchStudents();
        }
    }, [isOpen]);

    const fetchStudents = async () => {
        const { data, error } = await supabase
            .from('testprep_students')
            .select('id, first_name, last_name')
            .order('first_name');

        if (!error && data) {
            setStudents(data.map((s: any) => ({
                id: s.id,
                name: `${s.first_name} ${s.last_name}`
            })));
        }
    };

    const generateInstallments = () => {
        const total = Number(formData.totalAmount);
        if (total <= 0) return;

        // Peşin ödeme veya tekli taksit
        if (formData.paymentType === 'full') {
            setInstallments([{
                date: formData.firstPaymentDate,
                amount: total,
                status: 'pending'
            }]);
            return;
        }

        const count = Number(formData.installmentCount);
        if (count <= 0) return;

        const amountPerInstallment = Math.floor(total / count);
        const remainder = total % count;

        const newInstallments: Installment[] = [];
        let currentDate = new Date(formData.firstPaymentDate);

        for (let i = 0; i < count; i++) {
            const amount = i === 0 ? amountPerInstallment + remainder : amountPerInstallment;

            newInstallments.push({
                date: currentDate.toISOString().split('T')[0],
                amount: amount,
                status: 'pending'
            });

            currentDate.setMonth(currentDate.getMonth() + 1);
        }
        setInstallments(newInstallments);
    };

    const handleInstallmentChange = (index: number, field: keyof Installment, value: any) => {
        const updated = [...installments];
        updated[index] = { ...updated[index], [field]: value };
        setInstallments(updated);
    };

    const calculateTotalFromTable = () => {
        return installments.reduce((acc, curr) => acc + Number(curr.amount), 0);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const { error } = await supabase
                .from('testprep_payments')
                .insert({
                    student_id: formData.studentId,
                    total_amount: parseFloat(formData.totalAmount),
                    currency: formData.currency,
                    installments: installments,
                    notes: formData.notes,
                    company_entity: formData.companyEntity
                });

            if (error) throw error;

            if (onSave) onSave();
            onClose();
            alert('Ödeme planı başarıyla oluşturuldu.');

            // Reset form
            setFormData({
                studentId: '',
                totalAmount: '',
                currency: 'TRY',
                paymentType: 'installment',
                installmentCount: 3,
                firstPaymentDate: new Date().toISOString().split('T')[0],
                notes: '',
                companyEntity: ''
            });
            setInstallments([]);

        } catch (error: any) {
            console.error('Error saving payment plan:', error);
            alert('Hata oluştu: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl overflow-hidden border border-gray-200 dark:border-gray-800 flex flex-col max-h-[90vh]">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Yeni Ödeme Planı</h3>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto flex-1">

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Öğrenci</label>
                            <select
                                required
                                value={formData.studentId}
                                onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="">Seçiniz</option>
                                {students.map((student: any) => (
                                    <option key={student.id} value={student.id}>
                                        {student.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Para Birimi</label>
                            <select
                                value={formData.currency}
                                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="TRY">TRY (₺)</option>
                                <option value="USD">USD ($)</option>
                                <option value="EUR">EUR (€)</option>
                                <option value="GBP">GBP (£)</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Şirket</label>
                            <select
                                required
                                value={formData.companyEntity}
                                onChange={(e) => setFormData({ ...formData, companyEntity: e.target.value })}
                                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="">Seçiniz</option>
                                <option value="Pisa Edu">Pisa Edu</option>
                                <option value="Pisa Test Prep">Pisa Test Prep</option>
                            </select>
                        </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl space-y-4">
                        {/* Ödeme Tipi Seçimi */}
                        <div className="flex items-center gap-4 mb-4">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Ödeme Tipi:</label>
                            <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData({ ...formData, paymentType: 'full' });
                                        setInstallments([]);
                                    }}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${formData.paymentType === 'full'
                                        ? 'bg-green-600 text-white'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    Peşin
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData({ ...formData, paymentType: 'installment' });
                                        setInstallments([]);
                                    }}
                                    className={`px-4 py-2 text-sm font-medium transition-colors ${formData.paymentType === 'installment'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    Taksit
                                </button>
                            </div>
                        </div>

                        <div className="flex items-end space-x-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Toplam Tutar</label>
                                <input
                                    type="number"
                                    required
                                    placeholder="0.00"
                                    value={formData.totalAmount}
                                    onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })}
                                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                                />
                            </div>
                            {formData.paymentType === 'installment' && (
                                <div className="w-24">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Taksit</label>
                                    <input
                                        type="number"
                                        min="2"
                                        max="24"
                                        value={formData.installmentCount}
                                        onChange={(e) => setFormData({ ...formData, installmentCount: Number(e.target.value) })}
                                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            )}
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {formData.paymentType === 'full' ? 'Ödeme Tarihi' : 'İlk Ödeme'}
                                </label>
                                <input
                                    type="date"
                                    value={formData.firstPaymentDate}
                                    onChange={(e) => setFormData({ ...formData, firstPaymentDate: e.target.value })}
                                    className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={generateInstallments}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center mb-[1px]"
                            >
                                <Calculator className="w-4 h-4 mr-2" />
                                {formData.paymentType === 'full' ? 'Oluştur' : 'Hesapla'}
                            </button>
                        </div>
                    </div>

                    {/* Installments Table */}
                    {installments.length > 0 && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-800">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Taksit #</th>
                                        <th className="px-3 py-2 text-left">Vade Tarihi</th>
                                        <th className="px-3 py-2 text-left">Tutar</th>
                                        <th className="px-3 py-2 text-center">Durum</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {installments.map((inst, idx) => (
                                        <tr key={idx}>
                                            <td className="px-3 py-2 font-medium text-gray-500">
                                                {formData.paymentType === 'full' ? 'Peşin' : idx + 1}
                                            </td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="date"
                                                    value={inst.date}
                                                    onChange={(e) => handleInstallmentChange(idx, 'date', e.target.value)}
                                                    className="w-full bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white"
                                                />
                                            </td>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="number"
                                                    value={inst.amount}
                                                    onChange={(e) => handleInstallmentChange(idx, 'amount', e.target.value)}
                                                    className="w-full bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white font-medium"
                                                />
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-500">
                                                Bekliyor
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 dark:bg-gray-800 font-bold">
                                    <tr>
                                        <td colSpan={2} className="px-3 py-2 text-right">Toplam:</td>
                                        <td className="px-3 py-2 text-blue-600">
                                            {calculateTotalFromTable()} {formData.currency}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notlar</label>
                        <textarea
                            rows={2}
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                    </div>

                    <div className="flex justify-end pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={loading || installments.length === 0}
                            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {loading ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddPaymentPlanModal;
